import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { unreadCount } from '../api/notifications';
import { useAuth } from '../auth/useAuth';
import { NotificationsContext, type NotificationsState } from './NotificationsContext';

/**
 * How many notifications are unread, in one place.
 *
 * It lives above the routes rather than inside the shell because two parts of
 * the product need the same number: the badge that shows it, and the page that
 * changes it. Marking everything read and leaving the badge saying "5" is the
 * kind of small lie that makes people stop believing the badge.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { account } = useAuth();
  const [unread, setUnread] = useState(0);

  const refresh = useCallback(async () => {
    if (!account) {
      setUnread(0);
      return;
    }
    try {
      setUnread(await unreadCount());
    } catch {
      // A badge is not worth an error state. Show nothing rather than a guess.
      setUnread(0);
    }
  }, [account]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<NotificationsState>(() => ({ unread, refresh }), [unread, refresh]);

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}
